import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  JOURNEY_STEP_STATUS,
  JOURNEY_STEP_TYPE,
  TROPHY_CONFIG,
} from '../../../../services/utilityFunctions';
import translate from '../../../../commonComponents/translate';
import JourneyStepProp from '../../propTypes/journeyStepProp';
import ProfileIcon from './icons/ProfileIcon';
import CourseIcon from '../../../../commonComponents/icons/journey/CourseIcon';
import ChallengeIcon from '../../../../commonComponents/icons/journey/ChallengeIcon';
import TaskIcon from '../../../../commonComponents/icons/journey/TaskIcon';
import SurveyIcon from '../../../../commonComponents/icons/journey/SurveyIcon';
import PurchaseIcon from '../../../../commonComponents/icons/journey/PurchaseIcon';
import JourneyCarouselProgress from './JourneyCarouselProgress';
import CertificateIcon from './icons/CertificateIcon';
import DocumentIcon from '../../../../commonComponents/icons/journey/DocumentIcon';
import SessionIcon from '../../../../commonComponents/icons/journey/SessionIcon';
import LegalAndFinancesIcon from '../../../../commonComponents/icons/journey/LegalAndFinancesIcon';
import InfoIcon from '../../../../commonComponents/icons/journey/InfoIcon';
import CompanyIcon from '../../../../commonComponents/icons/journey/CompanyIcon';
import CareerIcon from '../../../../commonComponents/icons/journey/CareerIcon';
import ExperienceIcon from '../../../../commonComponents/icons/journey/ExperienceIcon';
import HobbiesIcon from '../../../../commonComponents/icons/journey/HobbiesIcon';
import SoftSkillsIcon from '../../../../commonComponents/icons/journey/SoftSkillsIcon';
import HardSkillsIcon from '../../../../commonComponents/icons/journey/HardSkillsIcon';
import LanguagesIcon from '../../../../commonComponents/icons/journey/LanguagesIcon';
import ContactsIcon from '../../../../commonComponents/icons/journey/ContactsIcon';
import MoodsIcon from '../../../../commonComponents/icons/journey/MoodsIcon';
import SignatureIcon from '../../../../commonComponents/icons/journey/SignatureIcon';
import SocialMediaIcon from '../../../../commonComponents/icons/journey/SocialMediaIcon';
import MediaIcon from '../../../../commonComponents/icons/journey/MediaIcon';
import MetaContext from '../../../../contextProviders/MetaContext';
import ProfileDocuments from '../../../../commonComponents/icons/journey/ProfileDocuments';
import FillAndSign from '../../../../commonComponents/icons/journey/FillAndSign';

const journeyStepTitle = (t, step) => {
  switch (step.type) {
    case JOURNEY_STEP_TYPE.TROPHY: {
      if (step.trophy !== 'custom') {
        return t(TROPHY_CONFIG[step.trophy].translationKey);
      }
      return step.trophyCustom && step.trophyCustom.name
        ? `${t('journey_stepType_trophy')}: ${step.trophyCustom.name}`
        : null;
    }
    case JOURNEY_STEP_TYPE.GAME:
      return step.game ? step.game.name : t('n/a');
    case JOURNEY_STEP_TYPE.COURSE:
      return step.course ? step.course.name : t('n/a');
    case JOURNEY_STEP_TYPE.SURVEY:
      return step.survey ? step.survey.title : t('n/a');
    case JOURNEY_STEP_TYPE.PROFILE_INFO:
      return t('journey_stepType_profile_info');
    case JOURNEY_STEP_TYPE.PROFILE_COMPANY:
      return t('journey_stepType_profile_company');
    case JOURNEY_STEP_TYPE.PROFILE_CAREER:
      return t('journey_stepType_profile_career');
    case JOURNEY_STEP_TYPE.PROFILE_EXPERIENCE:
      return t('journey_stepType_profile_experience');
    case JOURNEY_STEP_TYPE.PROFILE_HOBBIES:
      return t('journey_stepType_profile_hobbies');
    case JOURNEY_STEP_TYPE.PROFILE_SOFT_SKILLS:
      return t('journey_stepType_profile_soft_skills');
    case JOURNEY_STEP_TYPE.PROFILE_HARD_SKILLS:
      return t('journey_stepType_profile_hard_skills');
    case JOURNEY_STEP_TYPE.PROFILE_LANGUAGES:
      return t('journey_stepType_profile_languages');
    case JOURNEY_STEP_TYPE.PROFILE_CONTACTS:
      return t('journey_stepType_profile_contacts');
    case JOURNEY_STEP_TYPE.PROFILE_MOODS:
      return t('journey_stepType_profile_moods');
    case JOURNEY_STEP_TYPE.PROFILE_MEDIA:
      return t('journey_stepType_profile_media');
    case JOURNEY_STEP_TYPE.PROFILE_SOCIAL_MEDIA:
      return t('journey_stepType_profile_social_media');
    case JOURNEY_STEP_TYPE.PROFILE_LEGAL_FINANCES:
      return t('journey_stepType_profile_legal_finances');
    case JOURNEY_STEP_TYPE.PROFILE_DOCUMENTS:
      return t('journey_stepType_profile_documents');
    case JOURNEY_STEP_TYPE.PROFILE_FORMS:
      return t('journey_stepType_profile_forms');
    case JOURNEY_STEP_TYPE.MARKET_PRODUCT_PURCHASE:
      return t('journey_stepType_market_product_purchase');
    case JOURNEY_STEP_TYPE.SESSION:
      return step?.session?.name?.length > 0 ? step.session.name : t('session');
    default:
      return step.title;
  }
};

const journeyStepImage = step => {
  switch (step.type) {
    case JOURNEY_STEP_TYPE.TROPHY: {
      if (step.trophy !== 'custom') {
        return TROPHY_CONFIG[step.trophy].imageURL;
      }
      return step.trophyCustom && step.trophyCustom.icon
        ? step.trophyCustom.icon.thumb
        : null;
    }
    case JOURNEY_STEP_TYPE.GAME:
      return step.game && step.game.image ? step.game.image.thumb : null;
    case JOURNEY_STEP_TYPE.COURSE: {
      const imageSquare =
        step.course && step.course.imageSquare
          ? step.course.imageSquare.thumb
          : null;
      const image =
        step.course && step.course.image ? step.course.image.thumb : null;
      return imageSquare || image;
    }
    default:
      return null;
  }
};

const journeyStepIcon = (step, active) => {
  const { branding = {} } = useContext(MetaContext);
  const { primaryColor: brandColor } = branding || {};
  const primaryColor = brandColor || '#7ED321';
  const accentColor = '#FFFFFF';
  const mainColor = !active ? primaryColor : accentColor;
  const secondaryColor = !active ? accentColor : primaryColor;
  switch (step.type) {
    case JOURNEY_STEP_TYPE.GAME:
      return (
        <ChallengeIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.COURSE:
      return (
        <CourseIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.SURVEY:
    case JOURNEY_STEP_TYPE.PROFILE_FORMS:
      return (
        <SurveyIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.TASK:
      return <TaskIcon mainColor={mainColor} secondaryColor={secondaryColor} />;
    case JOURNEY_STEP_TYPE.PROFILE_INFO:
      return <InfoIcon mainColor={mainColor} secondaryColor={secondaryColor} />;
    case JOURNEY_STEP_TYPE.PROFILE_COMPANY:
      return (
        <CompanyIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.PROFILE_CAREER:
      return (
        <CareerIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.PROFILE_EXPERIENCE:
      return (
        <ExperienceIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.PROFILE_HOBBIES:
      return (
        <HobbiesIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.PROFILE_SOFT_SKILLS:
      return (
        <SoftSkillsIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.PROFILE_HARD_SKILLS:
      return (
        <HardSkillsIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.PROFILE_LANGUAGES:
      return (
        <LanguagesIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.PROFILE_CONTACTS:
      return (
        <ContactsIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.PROFILE_MOODS:
      return (
        <MoodsIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.PROFILE_MEDIA:
      return (
        <MediaIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.PROFILE_SOCIAL_MEDIA:
      return (
        <SocialMediaIcon
          mainColor={mainColor}
          secondaryColor={secondaryColor}
        />
      );
    case JOURNEY_STEP_TYPE.PROFILE_LEGAL_FINANCES:
      return (
        <LegalAndFinancesIcon
          mainColor={mainColor}
          secondaryColor={secondaryColor}
        />
      );
    case JOURNEY_STEP_TYPE.PROFILE_DOCUMENTS:
      return (
        <ProfileDocuments
          mainColor={mainColor}
          secondaryColor={secondaryColor}
        />
      );
    case JOURNEY_STEP_TYPE.SIGNATURE:
      return (
        <SignatureIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.MARKET_PRODUCT_PURCHASE:
      return (
        <PurchaseIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.SESSION:
      return (
        <SessionIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.DOCUMENT:
      return (
        <DocumentIcon mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.FILL_AND_SIGN:
      return (
        <FillAndSign mainColor={mainColor} secondaryColor={secondaryColor} />
      );
    case JOURNEY_STEP_TYPE.CERTIFICATE:
      return <CertificateIcon />;
    default:
      return <ProfileIcon />;
  }
};

const journeyStepHasProgress = step => {
  const typesWithoutProgress = [
    JOURNEY_STEP_TYPE.TASK,
    JOURNEY_STEP_TYPE.SURVEY,
    JOURNEY_STEP_TYPE.TROPHY,
    JOURNEY_STEP_TYPE.CERTIFICATE,
    JOURNEY_STEP_TYPE.SIGNATURE,
    JOURNEY_STEP_TYPE.FILL_AND_SIGN,
    JOURNEY_STEP_TYPE.MARKET_PRODUCT_PURCHASE,
    JOURNEY_STEP_TYPE.SESSION,
    JOURNEY_STEP_TYPE.DOCUMENT,
  ];
  return !typesWithoutProgress.includes(step.type);
};

function JourneyCarouselItem({
  t,
  step,
  index,
  stepCount,
  active,
  onClick,
  isSequential,
}) {
  let { status } = step;
  if (
    step.type !== JOURNEY_STEP_TYPE.TROPHY &&
    step.status === !JOURNEY_STEP_STATUS.LOCKED &&
    !isSequential
  ) {
    status = JOURNEY_STEP_STATUS.UNLOCKED;
  }
  const isTrophy = step.type === JOURNEY_STEP_TYPE.TROPHY;
  const isLocked = status === JOURNEY_STEP_STATUS.LOCKED;
  const isSelectable = !isTrophy && !isLocked;
  const isDone = status === JOURNEY_STEP_STATUS.DONE;
  const hasStatusIcon =
    status === JOURNEY_STEP_STATUS.DONE ||
    status === JOURNEY_STEP_STATUS.REJECTED ||
    status === JOURNEY_STEP_STATUS.WAITING_APPROVAL;

  return (
    <div
      className={cx('journey-overall-carousel-item', {
        active,
        selectable: isSelectable,
        locked: isLocked,
        done: isDone,
      })}
      onClick={isSelectable ? onClick : undefined}
    >
      {hasStatusIcon && (
        <div className="journey-overall-carousel-item-status">
          <img src={`/images/journey/${step.status}.svg`} alt="step status" />
        </div>
      )}
      <div className="journey-overall-carousel-item-step">
        <p>{`${index + 1}/${stepCount}`}</p>
      </div>
      <div className="image-container">
        {journeyStepImage(step) ? (
          <img
            src={journeyStepImage(step)}
            alt="step preview"
            className="challenge-image"
          />
        ) : (
          journeyStepIcon(step, active)
        )}
      </div>
      <h6 className="step-title">{journeyStepTitle(t, step)}</h6>
      <div className="progress-container-wrapper">
        {journeyStepHasProgress(step) && (
          <JourneyCarouselProgress step={step} active={active} />
        )}
      </div>
    </div>
  );
}

JourneyCarouselItem.propTypes = {
  t: PropTypes.func.isRequired,
  step: PropTypes.shape(JourneyStepProp.propType).isRequired,
  index: PropTypes.number.isRequired,
  stepCount: PropTypes.number.isRequired,
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  isSequential: PropTypes.bool.isRequired,
};

export default translate(JourneyCarouselItem);
